/* eslint-disable @typescript-eslint/no-explicit-any */
import { ContextMenu, ContextMenuRenderProps } from '@components/context-menu/ContextMenu';
import { ArrowDown, ArrowUp, Close } from '@components/icons/FarseerIcons';
import { Sheet, useSheetColumnConfigLocal, useSheetColumnHandlers } from '@components/sheet/Sheet';
import { ascImage, descImage } from '@components/sheet/sheetIcons';
import { NumberSheetColumn, SheetDataType, TextSheetColumn } from '@components/sheet/types';
import { evaluatorApi } from '@data/rest/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Modal } from '../../components/modal/Modal';
import { useI18nContext } from '../../i18n/i18n-react';
import { makeNumberCompare, makeStringCompare } from '@util/array';

const VARIABLE_STATS_KEY = ['variable-stats'];

type SortMode = 'asc' | 'desc' | 'off';

interface VariableStatsRow {
    variable: string;
    duration: number;
    cellCount: number;
    nonCalculated: number;
    calculated: number;
    error: string | null;
}

const enum ColumnType {
    Name = 'VariableStats-Name',
    Duration = 'VariableStats-Duration',
    CellCount = 'VariableStats-CellCount',
    NonCalculated = 'VariableStats-NonCalculated',
    Calculated = 'VariableStats-Calculated',
}

function useVariableStats() {
    const { data } = useQuery({
        queryKey: VARIABLE_STATS_KEY,
        queryFn: () => evaluatorApi.stats(),
        refetchOnWindowFocus: true,
        staleTime: 0,
    });

    const rows = useMemo(() => {
        const rows: VariableStatsRow[] = [];
        if (!data) {
            return rows;
        }
        const statsData = data as any;
        const variables = statsData.variables;

        for (const variable of variables) {
            rows.push({
                variable: variable.name,
                duration: variable.duration,
                cellCount: variable.calculated + variable.nonCalculated,
                nonCalculated: variable.nonCalculated,
                calculated: variable.calculated,
                error: variable.error,
            });
        }
        return rows;
    }, [data]);

    return {
        data,
        rows,
    };
}

function useVariableStatsTableData() {
    const { LL } = useI18nContext();
    const { rows, data } = useVariableStats();
    const [sortMode, setSortMode] = useState<SortMode>('off');
    const [sortColumn, setSortColumn] = useState<ColumnType>(ColumnType.Name);

    const sortedRows = useMemo(() => {
        if (sortMode === 'off') {
            return rows;
        }

        const sign = sortMode === 'asc' ? 1 : -1;

        const comparator = (() => {
            switch (sortColumn) {
                case ColumnType.Name:
                    return makeStringCompare((row: VariableStatsRow) => row.variable, sign);
                case ColumnType.Duration:
                    return makeNumberCompare((row: VariableStatsRow) => row.duration, sign);
                case ColumnType.CellCount:
                    return makeNumberCompare((row: VariableStatsRow) => row.cellCount, sign);
                case ColumnType.NonCalculated:
                    return makeNumberCompare((row: VariableStatsRow) => row.nonCalculated, sign);
                case ColumnType.Calculated:
                    return makeNumberCompare((row: VariableStatsRow) => row.calculated, sign);
            }
        })();

        return [...rows].sort(comparator);
    }, [rows, sortMode, sortColumn]);

    const sortIcon = sortMode === 'asc' ? ascImage : sortMode === 'desc' ? descImage : undefined;

    const columns = useMemo(() => {
        // eslint-disable-next-line react/display-name
        const makeColumnPopup = (columnType: ColumnType) => (props: ContextMenuRenderProps) => {
            const setSort = (mode: SortMode) => {
                setSortColumn(columnType);
                setSortMode(mode);
            };

            return (
                <ContextMenu {...props}>
                    <ContextMenu.Item
                        item={{
                            label: LL.ASCENDING(),
                            icon: ArrowUp,
                            action: () => setSort('asc'),
                        }}
                    />
                    <ContextMenu.Item
                        item={{
                            label: LL.DESCENDING(),
                            icon: ArrowDown,
                            action: () => setSort('desc'),
                        }}
                    />
                    <ContextMenu.Item
                        item={{
                            label: LL.UNSORT(),
                            icon: Close,
                            action: () => setSort('off'),
                        }}
                    />
                </ContextMenu>
            );
        };

        const getSortIcon = (columnType: ColumnType) => (sortColumn === columnType ? sortIcon : undefined);

        const nameColumn: TextSheetColumn<VariableStatsRow, void> = {
            key: ColumnType.Name,
            header: LL.NAME(),
            width: 200,
            type: SheetDataType.Text,
            order: 0,
            getter: (row) => row.variable,
            editColumnImage: getSortIcon(ColumnType.Name),
            editColumnPopup: makeColumnPopup(ColumnType.Name),
        };

        const durationColumn: NumberSheetColumn<VariableStatsRow, void> = {
            key: ColumnType.Duration,
            header: LL.DURATION(),
            width: 200,
            type: SheetDataType.Number,
            order: 1,
            getter: (row) => row.duration,
            editColumnImage: getSortIcon(ColumnType.Duration),
            editColumnPopup: makeColumnPopup(ColumnType.Duration),
        };

        const cellCountColumn: NumberSheetColumn<VariableStatsRow, void> = {
            key: ColumnType.CellCount,
            header: LL.CELL_COUNT(),
            width: 200,
            type: SheetDataType.Number,
            order: 2,
            getter: (row) => row.cellCount,
            editColumnImage: getSortIcon(ColumnType.CellCount),
            editColumnPopup: makeColumnPopup(ColumnType.CellCount),
        };

        const nonCalculated: NumberSheetColumn<VariableStatsRow, void> = {
            key: ColumnType.NonCalculated,
            header: LL.NONCALCULATED(),
            width: 200,
            type: SheetDataType.Number,
            order: 3,
            getter: (row) => row.nonCalculated,
            editColumnImage: getSortIcon(ColumnType.NonCalculated),
            editColumnPopup: makeColumnPopup(ColumnType.NonCalculated),
        };

        const calculatedColumn: NumberSheetColumn<VariableStatsRow, void> = {
            key: ColumnType.Calculated,
            header: LL.CALCULATED(),
            width: 200,
            type: SheetDataType.Number,
            order: 4,
            getter: (row) => row.calculated,
            editColumnImage: getSortIcon(ColumnType.Calculated),
            editColumnPopup: makeColumnPopup(ColumnType.Calculated),
        };

        return [nameColumn, durationColumn, cellCountColumn, nonCalculated, calculatedColumn];
    }, [LL, sortColumn, sortIcon]);

    return { columns, rows: sortedRows, data };
}

function VariableStatsSheet() {
    const { columns, rows, data } = useVariableStatsTableData();
    const { orderedColumns, setColumnConfig } = useSheetColumnConfigLocal(columns, `variable-stats`);
    const columnHandlers = useSheetColumnHandlers(orderedColumns);
    const rowKey = (row: VariableStatsRow) => row.variable;
    const d = data as any;

    const variablesWithErrors = useMemo(() => {
        if (!d?.variables) return [];
        return d.variables.filter((v: any) => v.error !== null);
    }, [d]);

    return (
        <div style={{ height: 600 }}>
            {data ? (
                <div style={{ height: variablesWithErrors.length > 0 ? 'auto' : 90, marginBottom: 10 }}>
                    <div>Total calculation time: {d.totalTime} ms</div>
                    <div>Model load time: {d.modelLoadTime} ms</div>
                    <div>Cell load time: {d.cellLoadTime} ms</div>
                    <div>Calculation time: {d.calculationTime} ms</div>
                    <div>Memory usage (rss): {d.memoryUsage.rss} MB</div>
                    {variablesWithErrors.length > 0 && (
                        <div style={{ marginTop: 10, padding: 10, backgroundColor: '#ffebee', borderRadius: 4 }}>
                            <div style={{ fontWeight: 'bold', color: '#c62828', marginBottom: 5 }}>
                                Variables with calculation errors ({variablesWithErrors.length}):
                            </div>
                            {variablesWithErrors.map((v: any) => (
                                <div key={v.name} style={{ marginLeft: 10, marginBottom: 5 }}>
                                    <strong>{v.name}:</strong> {v.error}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : null}
            <div style={{ height: 510 }}>
                <Sheet rows={rows} rowKey={rowKey} columnHandlers={columnHandlers} onColumnConfig={setColumnConfig} />
            </div>
        </div>
    );
}

function VariableStatsModal() {
    const { LL } = useI18nContext();

    return (
        <Modal.Content width={1000}>
            <Modal.Header title={LL.VARIABLE_METRICS()} />
            <Modal.Body>
                <VariableStatsSheet />
            </Modal.Body>
        </Modal.Content>
    );
}

export { VariableStatsModal };
